import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiMenu, FiBell, FiLogOut, FiSettings, FiCalendar, FiCheck, FiPlus } from "react-icons/fi";
import { useRecoilState } from "recoil";
import myInfoAtom from "../atoms/myInfoAtom";
import { signOut } from "../utils/authService";

function TopBar({ onMenuClick, reminders = [], onAcknowledge }) {
  const navigate = useNavigate();
  const [myInfo] = useRecoilState(myInfoAtom);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [acknowledgingId, setAcknowledgingId] = useState(null);
  const profileMenuRef = useRef(null);
  const notificationMenuRef = useRef(null);

  // Handle reminders that could be an array or object with data property
  const remindersList = Array.isArray(reminders) ? reminders : (reminders?.data || []);
  
  // Filter unacknowledged reminders for notification count
  const unacknowledgedReminders = remindersList.filter(r => !r.isAcknowledged);
  const notificationCount = unacknowledgedReminders.length;

  const handleAcknowledgeClick = (reminder) => {
    if (onAcknowledge) {
      setAcknowledgingId(reminder.id);
      onAcknowledge(reminder, () => setAcknowledgingId(null));
    }
  };

  const userName = myInfo?.name || "User";
  const userInitial = userName.charAt(0).toUpperCase();
  const userEmail = myInfo?.email || "";

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notificationMenuRef.current && !notificationMenuRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const handleProfileMenuClick = (e) => {
    e.stopPropagation();
    setShowProfileMenu(!showProfileMenu);
    setShowNotifications(false);
  };

  const handleNotificationClick = (e) => {
    e.stopPropagation();
    setShowNotifications(!showNotifications);
    setShowProfileMenu(false);
  };

  const handleSettingsClick = () => {
    console.log('Settings clicked');
    setShowProfileMenu(false);
    setTimeout(() => {
      console.log('Navigating to /setting');
      navigate('/setting');
    }, 50);
  };

  const handleSignOut = () => {
    console.log('Sign Out clicked');
    setShowProfileMenu(false);
    setTimeout(() => {
      console.log('Calling signOut function');
      signOut();
    }, 50);
  };

  return (
    <>
      {/* Desktop Top Bar */}
      <header className="hidden lg:flex items-center justify-between px-8 py-2 bg-white border-b border-neutral-200 shadow-sm sticky top-0 z-40">
        {/* Left side - Greeting */}
        <div className="flex items-center">
          <div className="flex flex-col gap-1">
            {/* <span className="text-xs font-semibold text-neutral-500 uppercase tracking-wide">Welcome</span> */}
            <span className="text-xl font-bold text-neutral-900">Hi {userName}! 👋</span>
          </div>
        </div>

        {/* Right side - Icons */}
        <div className="flex items-center gap-6">
          {/* Notification Bell */}
          <div className="relative" ref={notificationMenuRef}>
            <button
              onClick={handleNotificationClick}
              className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 transition-colors duration-200"
              aria-label="Notifications"
            >
              <FiBell size={22} strokeWidth={1.5} />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-danger text-white text-xs font-bold shadow-md">
                  {notificationCount > 9 ? "9+" : notificationCount}
                </span>
              )}
            </button>

            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-3 w-[calc(100vw-2rem)] max-w-sm sm:max-w-md lg:w-96 bg-white rounded-xl border border-neutral-200 shadow-xl z-50">
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-neutral-100">
                  <h3 className="text-sm sm:text-base font-bold text-neutral-900">Notifications</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notificationCount === 0 ? (
                    <div className="px-4 sm:px-6 py-8 text-center text-neutral-500 text-sm">
                      <FiBell size={24} className="mx-auto mb-2 opacity-30" />
                      <p>No notifications yet</p>
                    </div>
                  ) : (
                    <div className="py-2">
                      {unacknowledgedReminders.map((reminder) => {
                        const serviceRequest = reminder.fc_service_request;
                        const car = serviceRequest?.fleet_customer_car;
                        const vehicleInfo = car ? `${car.make} ${car.model} ${car.year}` : 'Unknown Vehicle';
                        const serviceType = serviceRequest?.serviceType === 'maintenance' ? 'Maintenance' : 'Service';
                        const isAcknowledging = acknowledgingId === reminder.id;
                        return (
                          <div
                            key={reminder.id}
                            className="px-3 sm:px-4 py-3 border-b border-neutral-100 last:border-b-0"
                          >
                            <div className="flex items-start gap-2 sm:gap-3">
                              <div className="flex h-8 w-8 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-amber-100 text-amber-600 flex-shrink-0">
                                <FiCalendar size={16} className="sm:w-[18px] sm:h-[18px]" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs sm:text-sm font-semibold text-neutral-900 break-words">{serviceType} Reminder</p>
                                <p className="text-[10px] sm:text-xs text-neutral-600 truncate">{vehicleInfo}</p>
                              </div>
                            </div>
                            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 mt-3 ml-0 sm:ml-13">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAcknowledgeClick(reminder);
                                }}
                                disabled={isAcknowledging}
                                className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <FiCheck size={14} />
                                {isAcknowledging ? 'Acknowledging...' : 'Acknowledge'}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowNotifications(false);
                                  navigate('/service_requests');
                                }}
                                className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full bg-primary text-white hover:bg-primary-dark transition-colors"
                              >
                                <FiPlus size={14} />
                                Service Request
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Profile Button */}
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={handleProfileMenuClick}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-neutral-300 bg-white hover:bg-neutral-50 transition-colors"
              aria-label="Profile menu"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white font-bold text-sm">
                {userInitial}
              </div>
              <div className="hidden sm:flex flex-col text-left">
                <span className="text-sm font-bold text-neutral-900">{userName}</span>
                <span className="text-xs text-neutral-600">{myInfo?.company_name || "Fleet Manager"}</span>
              </div>
            </button>

            {/* Profile Dropdown Menu */}
            {showProfileMenu && (
              <div 
                ref={profileMenuRef}
                className="absolute right-0 mt-3 w-72 bg-white rounded-xl border border-neutral-200 shadow-xl z-50"
              >
                {/* Profile Header */}
                <div className="px-2 py-2 border-b border-neutral-100">
                  <div className="flex items-center gap-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white font-bold text-xl flex-shrink-0">
                      {userInitial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-base font-bold text-neutral-900 truncate">{userName}</p>
                      <p className="text-xs text-neutral-600 break-all">{userEmail}</p>
                      {myInfo?.company_name && (
                        <p className="text-xs text-neutral-500 truncate">{myInfo.company_name}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  <button
                    type="button"
                    onClick={handleSettingsClick}
                    className="w-full flex items-center gap-3 px-6 py-3 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors text-left cursor-pointer"
                  >
                    <FiSettings size={20} strokeWidth={1.5} />
                    <span>Settings</span>
                  </button>
                </div>

                {/* Sign Out */}
                <div className="border-t border-neutral-100 px-3 py-2">
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-2 text-sm font-semibold text-white bg-danger hover:bg-danger-light rounded-lg transition-colors cursor-pointer"
                  >
                    <FiLogOut size={20} strokeWidth={1.5} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Top Bar */}
      <header className="lg:hidden flex items-center justify-between px-5 py-4 bg-white border-b border-neutral-200 shadow-sm sticky top-0 z-40">
        <button
          type="button"
          onClick={onMenuClick}
          aria-label="Open navigation"
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 transition-colors"
        >
          <FiMenu size={24} strokeWidth={1.5} />
        </button>

        {/* Mobile Greeting */}
        <div className="flex-1 ml-4">
          <span className="text-base font-bold text-neutral-900">Hi {userName}! 👋</span>
        </div>

        {/* Mobile Icons */}
        <div className="flex items-center gap-3">
          {/* Mobile Notification Bell */}
          <div className="relative" ref={notificationMenuRef}>
            <button
              onClick={handleNotificationClick}
              className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-100 transition-colors"
              aria-label="Notifications"
            >
              <FiBell size={22} strokeWidth={1.5} />
              {notificationCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-danger text-white text-xs font-bold shadow-md">
                  {notificationCount > 9 ? "9+" : notificationCount}
                </span>
              )}
            </button>

            {/* Mobile Notifications Dropdown */}
            {showNotifications && (
              <div className="fixed inset-x-3 mt-3 bg-white rounded-xl border border-neutral-200 shadow-xl z-50 sm:absolute sm:right-0 sm:left-auto sm:w-80 sm:inset-x-auto">
                <div className="px-4 py-3 border-b border-neutral-100">
                  <h3 className="text-sm font-bold text-neutral-900">Notifications</h3>
                </div>
                <div className="max-h-[70vh] sm:max-h-80 overflow-y-auto">
                  {notificationCount === 0 ? (
                    <div className="px-4 py-8 text-center text-neutral-500">
                      <FiBell size={28} className="mx-auto mb-3 opacity-30" />
                      <p className="text-xs">No notifications yet</p>
                    </div>
                  ) : (
                    <div className="py-2">
                      {unacknowledgedReminders.map((reminder) => {
                        const serviceRequest = reminder.fc_service_request;
                        const car = serviceRequest?.fleet_customer_car;
                        const vehicleInfo = car ? `${car.make} ${car.model} ${car.year}` : 'Unknown Vehicle';
                        const serviceType = serviceRequest?.serviceType === 'maintenance' ? 'Maintenance' : 'Service';
                        const isAcknowledging = acknowledgingId === reminder.id;
                        return (
                          <div
                            key={reminder.id}
                            className="px-3 py-3 border-b border-neutral-100 last:border-b-0"
                          >
                            <div className="flex items-start gap-2">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-100 text-amber-600 flex-shrink-0">
                                <FiCalendar size={14} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-neutral-900 break-words">{serviceType} Reminder</p>
                                <p className="text-[10px] text-neutral-600 truncate">{vehicleInfo}</p>
                                <p className="text-[10px] text-neutral-500 mt-1">Status: {serviceRequest?.status || 'Pending'}</p>
                              </div>
                            </div>
                            <div className="flex flex-col gap-2 mt-3">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAcknowledgeClick(reminder);
                                }}
                                disabled={isAcknowledging}
                                className="flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700 hover:bg-emerald-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full"
                              >
                                <FiCheck size={12} />
                                {isAcknowledging ? 'Acknowledging...' : 'Acknowledge'}
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowNotifications(false);
                                  navigate('/service_requests');
                                }}
                                className="flex items-center justify-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-full bg-primary text-white hover:bg-primary-dark transition-colors w-full"
                              >
                                <FiPlus size={12} />
                                Service Request
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Profile Button */}
          <div className="relative" ref={profileMenuRef}>
            <button
              onClick={handleProfileMenuClick}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white font-bold text-sm hover:bg-primary-dark transition-colors"
              aria-label="Profile menu"
            >
              {userInitial}
            </button>

            {/* Mobile Profile Dropdown */}
            {showProfileMenu && (
              <div 
                className="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-neutral-200 shadow-lg z-50"
              >
                <div className="px-6 py-5 border-b border-neutral-100">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white font-bold text-lg flex-shrink-0">
                      {userInitial}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-neutral-900 truncate">{userName}</p>
                      <p className="text-xs text-neutral-600 truncate">{userEmail}</p>
                    </div>
                  </div>
                </div>

                <div className="py-3">
                  <button
                    type="button"
                    onClick={handleSettingsClick}
                    className="w-full flex items-center gap-3 px-6 py-3 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors text-left cursor-pointer"
                  >
                    <FiSettings size={20} strokeWidth={1.5} />
                    <span>Settings</span>
                  </button>
                </div>

                <div className="border-t border-neutral-100 px-3 py-3">
                  <button
                    type="button"
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-6 py-3 text-sm font-semibold text-white bg-danger hover:bg-danger-light rounded-lg transition-colors cursor-pointer"
                  >
                    <FiLogOut size={20} strokeWidth={1.5} />
                    <span>Sign Out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
}

export default TopBar;
